import React, { useState, useEffect, useRef } from 'react';
import './AgentSelector.css';
import { API_URL } from '../config/api';

const AgentSelector = ({ phoneId, onAgentSelected }) => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const [selectingAgent, setSelectingAgent] = useState(null);
  const linkingInProgressRef = useRef(false); // Protection contre les doubles clics

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_URL}/api/users`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        // Filtrer les agents qui n'ont pas encore de phone_id (non liés)
        // phone_id peut être null, undefined, ou une chaîne vide
        const availableAgents = data.filter(agent => 
          !agent.phone_id || 
          agent.phone_id === '' || 
          agent.phone_id === null
        );
        setAgents(availableAgents);
        
        if (availableAgents.length === 0 && data.length > 0) {
          setError('Tous les agents sont déjà liés à un téléphone. Contactez un administrateur.');
        }
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Erreur inconnue' }));
        setError(`Erreur ${response.status}: ${errorData.error || 'Impossible de charger les agents'}`);
        console.error('Erreur API:', response.status, errorData);
      }
    } catch (err) {
      console.error('Erreur de connexion:', err);
      setError(`Erreur de connexion au serveur (${API_URL}). Vérifiez que le serveur est démarré et que vous êtes sur le même réseau.`);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAgent = async (agent) => {
    // Protection contre les doubles clics
    if (linkingInProgressRef.current) {
      console.log('⚠️ Une liaison est déjà en cours, ignore...');
      return;
    }

    setSelectingAgent(agent.id);
    setError(null);
    linkingInProgressRef.current = true;
    
    try {
      const response = await fetch(`${API_URL}/api/users/link-phone`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone_id: phoneId,
          user_id: agent.id
        }),
      });

      if (response.ok) {
        const data = await response.json();
        // Petit délai pour l'animation de succès
        setTimeout(() => {
          onAgentSelected(data.user);
          linkingInProgressRef.current = false;
        }, 300);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Erreur lors de la liaison du téléphone');
        setSelectingAgent(null);
        linkingInProgressRef.current = false;
      }
    } catch (err) {
      console.error('Erreur:', err);
      setError('Erreur de connexion au serveur');
      setSelectingAgent(null);
      linkingInProgressRef.current = false;
    }
  };

  // RGPD : Ne montrer les agents que si l'utilisateur a tapé son nom complet (prénom + nom)
  // Minimum 2 mots et 5 caractères pour afficher des résultats
  const searchWords = searchTerm.trim().split(/\s+/).filter(w => w.length > 0);
  const isFullNameSearch = searchWords.length >= 2 && searchTerm.trim().length >= 5;

  const filteredAgents = isFullNameSearch
    ? agents.filter(agent => {
        const agentName = agent.name.toLowerCase();
        // Vérifier que TOUS les mots de la recherche sont dans le nom
        return searchWords.every(word => agentName.includes(word.toLowerCase()));
      })
    : [];

  if (loading) {
    return (
      <div className="agent-selector">
        <div className="agent-selector-loading">
          <p>Chargement des agents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="agent-selector">
      <div className="agent-selector-header">
        <h2>👤 Identifiez-vous</h2>
        <p>Première utilisation : tapez votre <strong>nom complet</strong> (prénom et nom) pour vous identifier</p>
        <p className="agent-selector-subtitle">
          🔒 <strong>Protection des données (RGPD) :</strong> Aucun nom n'est affiché par défaut.
          Saisissez votre prénom et nom pour retrouver votre profil.
        </p>
        <p className="agent-selector-subtitle">
          ⚠️ <strong>Important :</strong> Votre téléphone sera lié à votre compte de manière <strong>permanente</strong>.
          Vous n'aurez plus besoin de vous identifier lors des prochains scans, mais vous ne pourrez plus changer de profil vous-même.
          Seul un administrateur pourra modifier cette liaison si vous changez de téléphone.
        </p>
      </div>

      {error && (
        <div className="agent-selector-error">
          <p>{error}</p>
        </div>
      )}

      <div className="agent-selector-search">
        <input
          type="text"
          placeholder="Tapez votre prénom et nom..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="agent-selector-input"
          autoFocus
        />
      </div>

      <div className="agent-selector-list">
        {!isFullNameSearch ? (
          <div className="agent-selector-empty">
            <p>🔒 Saisissez votre prénom et nom complet</p>
            <p className="agent-selector-hint">
              {searchTerm.trim().length === 0
                ? 'Exemple : Jean Dupont'
                : searchWords.length < 2
                  ? 'Veuillez entrer votre prénom ET votre nom de famille'
                  : 'Continuez à taper votre nom complet...'}
            </p>
          </div>
        ) : filteredAgents.length === 0 ? (
          <div className="agent-selector-empty">
            <p>Aucun agent trouvé pour "{searchTerm}"</p>
            <p className="agent-selector-hint">
              Vérifiez l'orthographe de votre nom ou contactez un administrateur.
            </p>
          </div>
        ) : (
          filteredAgents.map((agent) => (
            <div
              key={agent.id}
              className={`agent-selector-item ${selectingAgent === agent.id ? 'selecting' : ''}`}
              onClick={() => !selectingAgent && handleSelectAgent(agent)}
              style={{ opacity: selectingAgent && selectingAgent !== agent.id ? 0.5 : 1 }}
            >
              <div className="agent-selector-item-info">
                <h3>{agent.name || `Agent ${agent.id}`}</h3>
                {agent.email && (
                  <p className="agent-selector-item-email">{agent.email}</p>
                )}
                {agent.phone_id && agent.phone_id !== phoneId && (
                  <p className="agent-selector-item-id">ID: {agent.phone_id}</p>
                )}
              </div>
              {selectingAgent === agent.id ? (
                <div className="agent-selector-item-loading">
                  <div className="spinner-small"></div>
                </div>
              ) : (
                <div className="agent-selector-item-arrow">→</div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AgentSelector;
